export const generateUniqueId = () => {
  return '_' + Math.random().toString(36).substr(2, 9)
}

export const stripHtml = (html) => {
  if (!html) return ''
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return doc.body.textContent || ''
}

export const getYear = (dateString) => {
  if (!dateString) return 0
  try {
    const yearMatch = dateString.match(/^(\d{4})/)
    return yearMatch ? parseInt(yearMatch[1], 10) : 0
  } catch (e) {
    console.error("Failed to parse year from date string:", dateString, e)
    return 0
  }
}

export const formatDateForInput = (dateString) => {
  if (!dateString) return ''
  try {
    const date = new Date(dateString)
    const year = date.getFullYear()
    const month = ('0' + (date.getMonth() + 1)).slice(-2)
    return `${year}-${month}`
  } catch (e) {
    return ''
  }
}

export const normalizeDiplomas = (diplomas, source) => {
  return diplomas.map(d => {
    const normalized = {
      _id: d.id || generateUniqueId(),
      name: d.name || '',
    }

    if (source === 'analyse') {
      normalized.description = d.establishment || ''
      normalized.year = d.year ? `${d.year}-01-01 01:00:00` : ''
    } else {
      normalized.description = d.description || ''
      normalized.year = d.year || ''
    }

    return normalized
  })
}

export const combineAndDeduplicateDiplomas = (analyseDiplomas, freelanceDiplomas) => {
  const combined = []
  const initialSelectedDiplomas = {}
  const pairedFreelanceIds = new Set()

  analyseDiplomas.forEach(a => {
    const freelanceMatch = freelanceDiplomas.find(f =>
      (f.name.toLowerCase() === a.name.toLowerCase() || f.description.toLowerCase() === a.description.toLowerCase())
      && !pairedFreelanceIds.has(f._id)
    )

    if (freelanceMatch) {
      pairedFreelanceIds.add(freelanceMatch._id)

      const entry = {
        _id: a._id,
        name: a.name,
        analyseData: a,
        freelanceData: freelanceMatch,
        isNew: false,
      }

      if (
        a.description === freelanceMatch.description &&
        getYear(a.year) === getYear(freelanceMatch.year)
      ) {
        entry.isIdentical = true
        initialSelectedDiplomas[entry._id] = 'analyseData'
      } else {
        entry.isDifferent = true
        initialSelectedDiplomas[entry._id] = 'analyseData'
      }

      combined.push(entry)
    } else {
      combined.push({
        _id: a._id,
        name: a.name,
        analyseData: a,
        freelanceData: null,
        isNew: true,
      })

      initialSelectedDiplomas[a._id] = 'analyseData'
    }
  })

  freelanceDiplomas.forEach(f => {
    if (!pairedFreelanceIds.has(f._id)) {
      combined.push({
        _id: f._id,
        name: f.name,
        analyseData: null,
        freelanceData: f,
        isCurrent: true,
      })

      initialSelectedDiplomas[f._id] = 'freelanceData'
    }
  })

  combined.sort((a, b) => {
    const yearA = getYear(a.analyseData?.year || a.freelanceData?.year || '')
    const yearB = getYear(b.analyseData?.year || b.freelanceData?.year || '')
    return yearB - yearA
  })

  return { combined, initialSelectedDiplomas }
}

export const normalizeMissions = (missions, source) => {
  return missions.map(m => {
    const normalized = {
      _id: m.id || generateUniqueId(),
      title: m.title || '',
    }

    if (source === 'analyse') {
      normalized.company = m.companyName || ''
      normalized.description = m.details || ''
      normalized.start = m.startDate ? `${m.startDate} 01:00:00` : ''
      normalized.end = m.endDate ? `${m.endDate} 01:00:00` : ''
      normalized.current = m.current || false
      normalized.locality = null
    } else { // 'freelance'
      normalized.company = m.company || ''
      normalized.description = m.description || ''
      normalized.start = m.start || ''
      normalized.end = m.end || ''
      normalized.current = m.current || false
      normalized.locality = m.locality || ''
    }

    return normalized
  })
}

export const combineAndDeduplicateMissions = (analyseMissions, freelanceMissions) => {
  const combined = []
  const initialSelectedMissions = {}
  const pairedFreelanceIds = new Set()

  analyseMissions.forEach(a => {
    const descAnalyseRaw = stripHtml(a.description).trim().toLowerCase()
    const freelanceMatch = freelanceMissions.find(f => {
      if (pairedFreelanceIds.has(f._id)) return false
      const titleMatch = f.title.toLowerCase() === a.title.toLowerCase()
      const descFreelance = stripHtml(f.description).trim().toLowerCase()
      const descMatch = descAnalyseRaw !== '' && descAnalyseRaw === descFreelance
      return titleMatch || descMatch
    })

    if (freelanceMatch) {
      pairedFreelanceIds.add(freelanceMatch._id)

      const entry = {
        _id: a._id,
        name: a.title,
        analyseData: a,
        freelanceData: freelanceMatch,
        isNew: false,
      }

      const descAnalyse = stripHtml(a.description).trim()
      const descFreelance = stripHtml(freelanceMatch.description).trim()

      if (
        a.title.toLowerCase() === freelanceMatch.title.toLowerCase() &&
        a.company.toLowerCase() === freelanceMatch.company.toLowerCase() &&
        descAnalyse === descFreelance
      ) {
        entry.isIdentical = true
        initialSelectedMissions[entry._id] = 'analyseData'
      } else {
        entry.isDifferent = true
        initialSelectedMissions[entry._id] = 'analyseData'
      }

      combined.push(entry)
    } else {
      combined.push({
        _id: a._id,
        name: a.title,
        analyseData: a,
        freelanceData: null,
        isNew: true,
      })
      initialSelectedMissions[a._id] = 'analyseData'
    }
  })

  freelanceMissions.forEach(f => {
    if (!pairedFreelanceIds.has(f._id)) {
      combined.push({
        _id: f._id,
        name: f.title,
        analyseData: null,
        freelanceData: f,
        isCurrent: true,
      })
      initialSelectedMissions[f._id] = 'freelanceData'
    }
  })

  combined.sort((a, b) => {
    const dateA = new Date(a.analyseData?.start || a.freelanceData?.start || 0)
    const dateB = new Date(b.analyseData?.start || b.freelanceData?.start || 0)
    return dateB - dateA
  })

  return { combined, initialSelectedMissions }
}

export const normalizeCertifications = (certifications, source) => {
  return certifications.map(c => {
    const normalized = {
      _id: c.id || generateUniqueId(),
    }

    if (source === 'analyse') {
      normalized.label = c.name || ''
      normalized.date = c.year ? `${c.year}-01-01 01:00:00` : ''
    } else {
      normalized.label = c.label || ''
      normalized.date = c.date || ''
    }

    return normalized
  })
}

export const combineAndDeduplicateCertifications = (analyseCertifications, freelanceCertifications) => {
  const combined = []
  const initialSelectedCertifications = {}
  const pairedFreelanceIds = new Set()

  analyseCertifications.forEach(a => {
    const freelanceMatch = freelanceCertifications.find(f =>
      f.label.toLowerCase() === a.label.toLowerCase()
      && !pairedFreelanceIds.has(f._id)
    )

    if (freelanceMatch) {
      pairedFreelanceIds.add(freelanceMatch._id)

      const entry = {
        _id: a._id,
        label: a.label,
        analyseData: a,
        freelanceData: freelanceMatch,
        isNew: false,
      }

      if (getYear(a.date) === getYear(freelanceMatch.date)) {
        entry.isIdentical = true
        initialSelectedCertifications[entry._id] = 'analyseData'
      } else {
        entry.isDifferent = true
        initialSelectedCertifications[entry._id] = 'analyseData'
      }

      combined.push(entry)
    } else {
      combined.push({
        _id: a._id,
        label: a.label,
        analyseData: a,
        freelanceData: null,
        isNew: true,
      })

      initialSelectedCertifications[a._id] = 'analyseData'
    }
  })

  freelanceCertifications.forEach(f => {
    if (!pairedFreelanceIds.has(f._id)) {
      combined.push({
        _id: f._id,
        label: f.label,
        analyseData: null,
        freelanceData: f,
        isCurrent: true,
      })

      initialSelectedCertifications[f._id] = 'freelanceData'
    }
  })

  combined.sort((a, b) => {
    const yearA = getYear(a.analyseData?.date || a.freelanceData?.date || '')
    const yearB = getYear(b.analyseData?.date || b.freelanceData?.date || '')
    return yearB - yearA
  })

  return { combined, initialSelectedCertifications }
}

export const normalizeItSkills = (itSkills, source) => {
  return itSkills.map(s => {
    const normalized = {
      _id: s.id || generateUniqueId(),
    }

    if (source === 'analyse') {
      normalized.name = s || ''
    } else {
      normalized.name = s.name || ''
    }

    return normalized
  })
}

export const normalizeLanguages = (languages, source) => {
  return languages.map(l => {
    if (source === 'analyse') {
      return {
        _id: generateUniqueId(),
        languageId: l.id,
        name: '',
        level: l.levelKey || 1,
      }
    } else {
      return {
        _id: generateUniqueId(),
        languageId: l.language,
        name: l.name || '',
        level: l.level || 1,
      }
    }
  })
}

export const combineAndDeduplicateLanguages = (analyseLanguages, freelanceLanguages) => {
  const combined = []
  const initialSelectedLanguages = {}
  const pairedFreelanceIds = new Set()

  analyseLanguages.forEach(a => {
    const freelanceMatch = freelanceLanguages.find(f =>
      f.languageId === a.languageId && !pairedFreelanceIds.has(f._id)
    )

    if (freelanceMatch) {
      pairedFreelanceIds.add(freelanceMatch._id)

      const entry = {
        _id: a._id,
        name: freelanceMatch.name,
        analyseData: { ...a, name: freelanceMatch.name },
        freelanceData: freelanceMatch,
        isNew: false,
      }

      if (a.level === freelanceMatch.level) {
        entry.isIdentical = true
        initialSelectedLanguages[entry._id] = 'analyseData'
      } else {
        entry.isDifferent = true
        initialSelectedLanguages[entry._id] = 'analyseData'
      }

      combined.push(entry)
    } else {
      combined.push({
        _id: a._id,
        name: a.name || `Langue (ID: ${a.languageId})`,
        analyseData: a,
        freelanceData: null,
        isNew: true,
      })

      initialSelectedLanguages[a._id] = 'analyseData'
    }
  })

  freelanceLanguages.forEach(f => {
    if (!pairedFreelanceIds.has(f._id)) {
      combined.push({
        _id: f._id,
        name: f.name,
        analyseData: null,
        freelanceData: f,
        isCurrent: true,
      })

      initialSelectedLanguages[f._id] = 'freelanceData'
    }
  })

  combined.sort((a, b) => a.name.localeCompare(b.name))

  return { combined, initialSelectedLanguages }
}

export const combineAndDeduplicateItSkills = (analyseSkills, freelanceSkills) => {
  const combined = []
  const initialSelectedItSkills = {}
  const pairedFreelanceIds = new Set()

  analyseSkills.forEach(a => {
    const freelanceMatch = freelanceSkills.find(f =>
      f.name.toLowerCase() === a.name.toLowerCase()
      && !pairedFreelanceIds.has(f._id)
    )

    if (freelanceMatch) {
      pairedFreelanceIds.add(freelanceMatch._id)

      const entry = {
        _id: a._id,
        name: a.name,
        analyseData: a,
        freelanceData: freelanceMatch,
        isIdentical: true,
      }

      initialSelectedItSkills[entry._id] = 'analyseData'
      combined.push(entry)
    } else {
      combined.push({
        _id: a._id,
        name: a.name,
        analyseData: a,
        freelanceData: null,
        isNew: true,
      })

      initialSelectedItSkills[a._id] = 'analyseData'
    }
  })

  freelanceSkills.forEach(f => {
    if (!pairedFreelanceIds.has(f._id)) {
      combined.push({
        _id: f._id,
        name: f.name,
        analyseData: null,
        freelanceData: f,
        isCurrent: true,
      })

      initialSelectedItSkills[f._id] = 'freelanceData'
    }
  })

  combined.sort((a, b) => a.name.localeCompare(b.name))

  return { combined, initialSelectedItSkills }
}
